import { createContext, useContext, type PropsWithChildren } from "react";
import { c1_state } from "../model/C1_runtimeContext";
import type { C1_FurnitureProps } from "../types/C1_FurnitureTypes";
import { isUiModeDeveloper } from "../../../src/uiMode";

interface C1_FurnitureSvgProps extends PropsWithChildren<C1_FurnitureProps> {
  viewBoxX?: number;
  viewBoxY?: number;
  viewBoxWidth: number;
  viewBoxHeight: number;
  preserveAspectRatio?: string;
  transformOrigin?: string;
}

type C1_FurnitureUprightContentProps = PropsWithChildren<{
  anchorX: number;
  anchorY: number;
  ownerRotation?: number;
  ownerReflect?: boolean;
}>;

export const C1_FurnitureVectorContext = createContext(false);
export const C1_FurniturePlacementPreviewContext = createContext(false);

/** Hält Symbol- oder Textinhalt trotz Drehung und Spiegelung des Möbelkörpers aufrecht. */
export function C1_FurnitureUprightContent({
  anchorX,
  anchorY,
  ownerRotation = 0,
  ownerReflect = false,
  children,
}: C1_FurnitureUprightContentProps) {
  return (
    <g
      transform={`translate(${anchorX} ${anchorY}) scale(${ownerReflect ? -1 : 1} 1) rotate(${-ownerRotation}) translate(${-anchorX} ${-anchorY})`}
    >
      {children}
    </g>
  );
}

export function C1_FurnitureSvg({
  width,
  height,
  rotation = 0,
  viewBoxX = 0,
  viewBoxY = 0,
  viewBoxWidth,
  viewBoxHeight,
  preserveAspectRatio,
  originXPercentage = 0,
  originYPercentage = 0,

  children,
}: C1_FurnitureSvgProps) {
  const vectorMode = useContext(C1_FurnitureVectorContext);
  const resolvedWidth = width ?? viewBoxWidth;
  const resolvedHeight = height ?? viewBoxHeight;

  const transformOrigin = `${originXPercentage}% ${originYPercentage}%`;
  const originX = viewBoxX + (originXPercentage * viewBoxWidth) / 100;
  const originY = viewBoxY + (originYPercentage * viewBoxHeight) / 100;
  const markerRadiusX = (6 * viewBoxWidth) / Math.max(resolvedWidth, 1);
  const markerRadiusY = (6 * viewBoxHeight) / Math.max(resolvedHeight, 1);
  const showFurnitureOrigin =
    isUiModeDeveloper() && c1_state.showDebugFurnitureOrigins.value && c1_state.floorImageRenderOptions.value === null;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={resolvedWidth}
      height={resolvedHeight}
      viewBox={`${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`}
      preserveAspectRatio={preserveAspectRatio}
      x={vectorMode ? (-originXPercentage * resolvedWidth) / 100 : undefined}
      y={vectorMode ? (-originYPercentage * resolvedHeight) / 100 : undefined}
      style={{
        position: vectorMode ? undefined : "absolute",
        left: vectorMode ? undefined : originXPercentage ? `${(-originXPercentage * resolvedWidth) / 100}px` : "0px",
        top: vectorMode ? undefined : originYPercentage ? `${(-originYPercentage * resolvedHeight) / 100}px` : "0px",
        width: resolvedWidth,
        height: resolvedHeight,
        display: "block",
        overflow: "visible",
        transform: vectorMode || rotation === 0 ? undefined : `rotate(${rotation}deg)`,
        transformOrigin: vectorMode ? undefined : transformOrigin,
      }}
    >
      {children}
      {showFurnitureOrigin && (
        <ellipse
          cx={originX}
          cy={originY}
          rx={markerRadiusX}
          ry={markerRadiusY}
          fill="#00e640"
          stroke="#064e1f"
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
          pointerEvents="none"
          data-c1-debug="furniture-origin"
        />
      )}
    </svg>
  );
}
